import { DataGridCellValue, DataGridColumnConfig, DataGridRowConfig } from "components/DataGrid/types";
import { getColumnSortState, getVisibleColumns } from "components/DataGrid/utils";
import { DataGridWithFilter } from "components/DataGrid";
import { DataGridBody } from "components/DataGrid/DataGridBody";
import { DataGridFilter } from "components/DataGrid/DataGridFilter";
import { DataGridHead } from "components/DataGrid/DataGridHead";
import { DataGridHeader } from "components/DataGrid/DataGridHeader";
import { Button } from "components/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RelationsManagementGridColumnKeys } from "../types";
import React, { useCallback, useMemo, useState } from "react";
import { SubmitButton } from "components/Button/SubmitButton";
import { Modal } from "react-bootstrap";
import { getErrorMessage } from "components/utils/http";
import { useToast } from "components/Toast";
import { relateApplication } from "components/utils";
import cn from "classnames";
import { DataGridCellBadge } from "components/DataGrid/components/DataGridCellBadge";
import useMediaQuery from "components/utils/useMediaQuery";
import { useFilter } from "components/DataGrid/useFilter";

export const RelationManagementGrid: React.FC<{
    gridConfig: any;
    items: any[] | undefined;
    sortBy: string;
    sortAsc: boolean;
    onSortChange: (key: string, forceAsc?: boolean) => void;
    onFilterChange: (filter: string) => void;
    isLoadingGrid: boolean;
    applicationNumber: string;
    onRelationsUpdated: () => void;
    selectedRows: Set<string>;
    setSelectedRows: React.Dispatch<React.SetStateAction<Set<string>>>;
    totalRecords?: number;
    projectNumber?: string;
}> = ({
    projectNumber,
    items,
    gridConfig,
    sortBy,
    sortAsc,
    totalRecords,
    onSortChange,
    onFilterChange,
    isLoadingGrid,
    applicationNumber,
    onRelationsUpdated,
    selectedRows,
    setSelectedRows,
}) => {
    const columns = getVisibleColumns(gridConfig);
    const [showModal, setShowModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isTargetAppRelated, setIsTargetAppRelated] = useState(false);
    const [selectedApp, setSelectedApp] = useState<string | null>("");
    const toast = useToast();
    const [modalBulkDescription, setModalBulkDescription] = useState("");
    const [modalBulkSelectedRow, setModalBulkSelectedRow] = useState(new Set<string>());
    const isMobile = useMediaQuery("(max-width: 768px)");
    const mobileConfig = {
        title: RelationsManagementGridColumnKeys.projectName,
        subtitle: RelationsManagementGridColumnKeys.projectNumber,
        columns: ["firstname", "lastname", "program", "appid"],
    };

    const onRelateApplication = useCallback(
        async (targetApplicationNumbers: Set<string> | string) => {
            const targetApplications = Array.from(
                typeof targetApplicationNumbers === "string" ? [targetApplicationNumbers] : targetApplicationNumbers
            );
            setIsSubmitting(true);
            let response;
            try {
                if (applicationNumber) {
                    response = await relateApplication(applicationNumber, targetApplications);
                    setSelectedApp("");
                    setSelectedRows(new Set([]));
                }
            } catch (error) {
                toast.error(getErrorMessage(error));
                setIsSubmitting(false);
            } finally {
                toast.success(response?.responseMessage);
                setIsSubmitting(false);
                setShowModal(false);
                setShowBulkModal(false);
                onRelationsUpdated();
            }
        },
        [applicationNumber, onRelationsUpdated, toast, setSelectedRows]
    );

    const rows: DataGridRowConfig[] = (items || []).map((item, index) => ({
        index,
        data: item,
        actions: [
            {
                content: () => (
                    <Button
                        style={
                            !item.isRelated
                                ? {
                                      borderColor: "var(--bg-active) !important",
                                  }
                                : {}
                        }
                        variant={item.isRelated ? "outline-danger" : "outline-secondary"}
                        className={cn("text-decoration-none d-flex align-items-center gap-1", {
                            "border-0": !isMobile,
                        })}
                        onClick={() => handleApplicationRelation(item[RelationsManagementGridColumnKeys.appId], item.isRelated)}
                        title={item.isRelated ? "Remove Relation" : "Add Relation"}
                        aria-label={item.isRelated ? "Remove Relation" : "Add Relation"}
                        aria-haspopup="dialog"
                    >
                        <FontAwesomeIcon icon={item.isRelated ? ["fal", "link-slash"] : ["fal", "link"]} size="lg" />
                        {isMobile && <span> {item.isRelated ? "Remove" : "Relate"}</span>}
                    </Button>
                ),
            },
        ],
        mobileConfig,
    }));

    const isAllRowsSelected = rows.length > 0 && selectedRows.size > 0 && rows.length === selectedRows.size;

    const handleApplicationRelation = (appId: string, isRelated: boolean) => {
        setSelectedApp(appId);
        setIsTargetAppRelated(isRelated);
        setShowModal(true);
    };

    const ColumnBadge = (column: DataGridColumnConfig, _: DataGridCellValue, row: DataGridRowConfig) => {
        return <DataGridCellBadge item={row.data[column.key] ?? ""} style={{ backgroundColor: "rgb(232, 232, 240)" }} />;
    };

    const getColumnRenderer = (column: DataGridColumnConfig) => {
        const mapper: Record<string, typeof ColumnBadge> = {
            projectnumber: ColumnBadge,
            appid: ColumnBadge,
        };

        return mapper[column.columnname.toLocaleLowerCase()];
    };
    const formattedColumns: DataGridColumnConfig[] = columns.flatMap((col) => {
        const renderer = getColumnRenderer(col);

        const bodyColumns = mobileConfig.columns.map((i) => i.toLowerCase());
        const headerColumns = [mobileConfig.title, mobileConfig.subtitle].map((i) => i.toLowerCase());
        const bodyColumnsIncludes = bodyColumns.includes(col.columnname.toLowerCase());
        const headerColumnsIncludes = headerColumns.includes(col.key.toLowerCase() as any);
        if (isMobile && !bodyColumnsIncludes && !headerColumnsIncludes) {
            return [];
        }

        return [
            {
                ...col,
                sort: getColumnSortState(col, sortBy, sortAsc),
                name: col.name.split("_").join(" "),
                className: !isMobile && col.columnname === "program" ? "min-w-200px" : undefined,
                renderer,
            },
        ];
    });

    const handleSelectAllRows = (toggleFilter?: () => void, isFilterOpen?: boolean) => {
        return (forceUnselectAll?: boolean) => {
            const updatedSelectedRows = new Set(selectedRows);

            if (isAllRowsSelected || forceUnselectAll) {
                setSelectedRows(new Set([]));
                return;
            }

            isFilterOpen && toggleFilter?.();

            rows.forEach((row) => {
                const appId = row.data[RelationsManagementGridColumnKeys.appId];
                if (!appId) return;

                updatedSelectedRows.add(appId);
            });
            setSelectedRows(updatedSelectedRows);
        };
    };

    const handleSelectRow = (
        toggleFilter?: ReturnType<typeof useFilter>["toggleFilter"],
        isFilterEmpty?: ReturnType<typeof useFilter>["isFilterEmpty"]
    ) => {
        return (row: DataGridRowConfig) => {
            const appId = row.data[RelationsManagementGridColumnKeys.appId];
            const updatedSelectedRows = new Set(selectedRows);

            if (!appId) return;

            !isMobile && toggleFilter?.(!isFilterEmpty);

            if (updatedSelectedRows.has(appId)) {
                updatedSelectedRows.delete(appId);
            } else {
                updatedSelectedRows.add(appId);
            }

            setSelectedRows(updatedSelectedRows);
        };
    };

    const isRowSelected = useCallback(
        (row: DataGridRowConfig) => {
            const appId = row.data[RelationsManagementGridColumnKeys.appId];
            return appId !== null && selectedRows.has(appId);
        },
        [selectedRows]
    );

    const SelectedActionsRowComponent = useMemo(() => {
        if (!items) return null;

        const relatedApplications = Array.from(selectedRows).filter((key) => {
            const item = items.find((i) => i[RelationsManagementGridColumnKeys.appId] === key);
            return item?.isRelated === "1";
        });

        const unrelatedApplications = Array.from(selectedRows).filter((key) => {
            const item = items.find((i) => i[RelationsManagementGridColumnKeys.appId] === key);
            return item?.isRelated !== "1";
        });

        const hasBoth = !relatedApplications.length && !unrelatedApplications.length;

        const handleOnClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            const name = e.currentTarget.name;

            if (name === "relate") {
                setModalBulkSelectedRow(new Set(unrelatedApplications));
                setModalBulkDescription(`Are you sure you want to relate these applications to #${projectNumber}?`);
                setShowBulkModal(true);
                return;
            }

            setModalBulkSelectedRow(new Set(relatedApplications));
            setModalBulkDescription(`Are you sure you want to unrelate these applications to #${projectNumber}?`);
            return setShowBulkModal(true);
        };

        return (
            <>
                {(unrelatedApplications.length || hasBoth) && (
                    <Button
                        variant="outline-secondary"
                        className="d-flex align-items-center gap-1 py-1 px-2 border flex-shrink-0"
                        style={{
                            color: "var(--bs-table-color)",
                            borderColor: "var(--bg-active) !important",
                        }}
                        onClick={handleOnClick}
                        title="Relate Selected Applications"
                        aria-haspopup="dialog"
                        name="relate"
                    >
                        <FontAwesomeIcon className="visible" icon={["fal", "link"]} /> Relate
                    </Button>
                )}
                {(relatedApplications.length || hasBoth) && (
                    <Button
                        variant="outline-danger"
                        className="d-flex align-items-center gap-1 py-1 px-2 flex-shrink-0"
                        onClick={handleOnClick}
                        title="Unrelate Selected Applications"
                        aria-haspopup="dialog"
                    >
                        <FontAwesomeIcon className="visible" icon={["fal", "link-slash"]} /> Remove
                    </Button>
                )}
            </>
        );
    }, [projectNumber, items, selectedRows]);

    return (
        <>
            <DataGridWithFilter onFilterChange={onFilterChange} onSortChange={onSortChange} gridConfig={gridConfig} withWrapper={false}>
                {({
                    isFilterOpen,
                    isSortOpen,
                    isFilterEmpty,
                    isFilterChanged,
                    toggleFilter,
                    toggleSort,
                    getFilterValue,
                    onFilterValueChange,
                    onFilterValueKeyDown,
                    onFieldClean,
                    onFilterSubmit,
                    onFilterClear,
                    onSortClear,
                    filterSelected,
                    filter,
                    onFilterInputFocus,
                    setFilterInputRef,
                }) => (
                    <>
                        <DataGridHead className={cn({ "position-sticky sticky-top": !isMobile })}>
                            <DataGridHeader
                                toggleFilter={toggleFilter}
                                toggleSort={toggleSort}
                                isFilterOpen={isFilterOpen}
                                isSortOpen={isSortOpen}
                                filter={filter}
                                sortBy={sortBy}
                                sortAsc={sortAsc}
                                columns={formattedColumns}
                                totalRecords={totalRecords}
                                totalSelectedRecords={selectedRows.size}
                                SelectedActionsRowComponent={SelectedActionsRowComponent}
                                onSortChange={onSortChange}
                                onFilterClear={onFilterClear}
                                onSortClear={onSortClear}
                                isFilterEmpty={isFilterEmpty}
                                stickyActionsColumn
                                canSelectRows
                                allRowsSelected={isAllRowsSelected}
                                handleSelectAllRows={handleSelectAllRows(toggleFilter, isFilterOpen)}
                                onFilterSubmit={onFilterSubmit}
                                onFilterInputFocus={onFilterInputFocus}
                                isFilterChanged={isFilterChanged}
                                getFilterValue={getFilterValue}
                                onFieldClean={onFieldClean}
                            />
                            <DataGridFilter
                                setFilterInputRef={setFilterInputRef}
                                canSelectRows
                                toggleFilter={toggleFilter}
                                toggleSort={toggleSort}
                                isFilterOpen={isFilterOpen}
                                isSortOpen={isSortOpen}
                                isFilterEmpty={isFilterEmpty}
                                columns={formattedColumns}
                                filter={filter}
                                sortBy={sortBy}
                                sortAsc={sortAsc}
                                onFilterChange={onFilterChange}
                                getFilterValue={getFilterValue}
                                onFilterValueChange={onFilterValueChange}
                                onFilterValueKeyDown={onFilterValueKeyDown}
                                onFilterClear={onFilterClear}
                                onSortClear={onSortClear}
                                onSortChange={onSortChange}
                                onFieldClean={onFieldClean}
                                onFilterSubmit={onFilterSubmit}
                                filterSelected={filterSelected}
                                onFilterInputFocus={onFilterInputFocus}
                            />
                        </DataGridHead>
                        <DataGridBody
                            rows={rows}
                            columns={formattedColumns}
                            isLoadingData={isLoadingGrid}
                            canSelectRows
                            handleSelectRow={handleSelectRow(toggleFilter, isFilterEmpty)}
                            isRowSelected={isRowSelected}
                            isFilterEmpty={isFilterEmpty}
                            onFilterClear={onFilterClear}
                        />
                    </>
                )}
            </DataGridWithFilter>
            {showModal && (
                <SingleApplicationRelationModal
                    projectNumber={projectNumber}
                    isTargetAppRelated={isTargetAppRelated}
                    onRelateApplication={onRelateApplication}
                    selectedApp={selectedApp}
                    isSubmitting={isSubmitting}
                    setShowModal={setShowModal}
                />
            )}
            {showBulkModal && (
                <BulkApplicationRelationModal
                    onRelateApplication={onRelateApplication}
                    selectedRows={modalBulkSelectedRow}
                    isSubmitting={isSubmitting}
                    setShowBulkModal={setShowBulkModal}
                    description={modalBulkDescription}
                />
            )}
        </>
    );
};

const BulkApplicationRelationModal = ({
    setShowBulkModal,
    onRelateApplication,
    selectedRows,
    isSubmitting,
    description,
}: {
    setShowBulkModal: React.Dispatch<React.SetStateAction<boolean>>;
    onRelateApplication: any;
    selectedRows: Set<string>;
    isSubmitting: boolean;
    description: string;
}) => {
    return (
        <Modal show onHide={() => setShowBulkModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Relate Applications</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>{description}</p>
            </Modal.Body>
            <Modal.Footer>
                <SubmitButton
                    onClick={() => {
                        onRelateApplication(selectedRows);
                    }}
                    isSubmitting={isSubmitting}
                    variant="primary"
                    spinnerText="Is Relating..."
                >
                    Yes
                </SubmitButton>
                <Button onClick={() => setShowBulkModal(false)} variant="secondary">
                    No
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
const SingleApplicationRelationModal = ({
    projectNumber,
    isTargetAppRelated,
    onRelateApplication,
    selectedApp,
    isSubmitting,
    setShowModal,
}: {
    projectNumber?: string;
    isTargetAppRelated: boolean;
    onRelateApplication: any;
    selectedApp: string | null;
    isSubmitting: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    return (
        <Modal show onHide={() => setShowModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>{!isTargetAppRelated ? "Relate Application" : "Remove Relation"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {`Are you sure you want to ${!isTargetAppRelated ? "add this relation to" : "remove this relation from"}`}
                <b> #{projectNumber}</b>?
            </Modal.Body>
            <Modal.Footer>
                <SubmitButton
                    onClick={() => {
                        onRelateApplication(selectedApp!);
                    }}
                    isSubmitting={isSubmitting}
                    variant="primary"
                    spinnerText={isTargetAppRelated ? "Is Removing..." : "Is Relating..."}
                >
                    Yes
                </SubmitButton>
                <Button onClick={() => setShowModal(false)} variant="secondary">
                    No
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
